package ui.device.detail.preview.screen

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import api.ClientAppType
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.Cellphone_arrow_down
import compose.assets.svg.Check
import compose.assets.svg.Chevron_right
import compose.assets.svg.Close
import org.ssoft.common.type.ResolutionStandard
import org.ssoft.common.type.ScreenRatio
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.*
import tools.await
import tools.findRoot
import tools.rememberString
import ui.app.assign.detail.AppDetailArgs
import ui.company.detail.CompanyArgs
import ui.device.detail.editor.DeviceEditorScope
import ui.device.detail.preview.DevicePreviewScope
import ui.device.detail.preview.DevicePreviewVM
import ui.hardware.detail.HardwareDetailArgs
import ui.location.detail.LocationArgs
import ui.resource.detail.ResourceArgs
import ui.widget.LazyImage
import ui.widget.TwoFieldText
import ui.widget.TwoFieldTextIcon

@Composable
fun DevicePreviewScope.DeviceScreen01_Info(nnvl: DevicePreviewScope.Wrapper) {
    LazyVerticalStaggeredGrid(
        columns = StaggeredGridCells.FixedSize(512.dp),
        contentPadding = PaddingValues(vertical = 16.dp),
        horizontalArrangement = Arrangement.SpaceEvenly,
        verticalItemSpacing = 16.dp,
        modifier = Modifier.fillMaxSize()
    ) {
        if (nnvl.data.emergency.active) {
            item { CardEmergency(nnvl) }
        }

        item { CardPrimary(nnvl) }
        item { CardSecondary(nnvl) }

        if (nnvl.data.photoResId > 0) {
            item { CardPhoto(nnvl) }
        }

        item { CardVersions(nnvl) }
    }
}

@Composable
private fun DevicePreviewScope.CardVersions(nnvl: DevicePreviewScope.Wrapper) {
    if (nnvl.data.apps.isEmpty() || nnvl.data.apps.any { it.version <= 0 }) {
        Card(
            onClick = { action(DevicePreviewVM.Action.DevAction.Update) },
            colors = CardDefaults.cardColors(
                containerColor = MaterialTheme.colorScheme.surfaceContainer
            )
        ) {
            Row(Modifier.padding(24.dp)) {
                Text(
                    text = Res.string.device_detail__update_required.rememberString(),
                    modifier = Modifier.weight(1F)
                        .align(Alignment.CenterVertically)
                )
                Icon(
                    imageVector = Svg.Chevron_right,
                    contentDescription = null,
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
            }
        }
    } else {
        CommonCardWrapper {
            Column(
                Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier.alpha(0.7F)
                ) {
                    Icon(
                        imageVector = Svg.Cellphone_arrow_down,
                        contentDescription = null,
                        modifier = Modifier.size(18.dp)
                    )
                    Text(
                        text = Res.string.device_detail__update_title.rememberString(),
                        style = MaterialTheme.typography.labelMedium
                    )
                }
                HorizontalDivider()
                Row(Modifier.fillMaxWidth()) {
                    Text(
                        text = Res.string.device_detail__update_label_name.rememberString(),
                        color = MaterialTheme.colorScheme.onSurface,
                        modifier = Modifier.weight(2F)
                            .alpha(0.7F)
                    )
                    Text(
                        text = Res.string.device_detail__update_label_version.rememberString(),
                        textAlign = TextAlign.Right,
                        color = MaterialTheme.colorScheme.onSurface,
                        modifier = Modifier.weight(1F)
                            .alpha(0.7F)
                    )
                    Text(
                        text = Res.string.device_detail__update_label_latest.rememberString(),
                        textAlign = TextAlign.Right,
                        color = MaterialTheme.colorScheme.onSurface,
                        modifier = Modifier.weight(1F)
                            .alpha(0.7F)
                    )
                }
                nnvl.data.apps.forEach {
                    Row(Modifier.fillMaxWidth()) {
                        val color = if (it.lastStable - it.version > 10) MaterialTheme.colorScheme.error
                        else MaterialTheme.colorScheme.onSurface
                        Text(
                            text = ClientAppType[it.id].label,
                            modifier = Modifier.weight(2F),
                            color = color
                        )
                        Text(
                            text = "${it.version}",
                            textAlign = TextAlign.Right,
                            modifier = Modifier.weight(1F),
                            color = color
                        )
                        if (it.version == it.lastStable || it.lastStable <= 0) {
                            Box(Modifier.weight(1F)) {
                                Icon(
                                    imageVector = Svg.Check,
                                    contentDescription = null,
                                    tint = color,
                                    modifier = Modifier.align(Alignment.CenterEnd)
                                )
                            }
                        } else {
                            Text(
                                text = "${it.lastStable}",
                                textAlign = TextAlign.Right,
                                modifier = Modifier.weight(1F),
                                color = color
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun DevicePreviewScope.CardPhoto(nnvl: DevicePreviewScope.Wrapper) = Card(
    enabled = nnvl.canViewResources,
    onClick = {
        nav.navTo_ResourceUI(
            ResourceArgs(
                companyId = nnvl.data.company.id,
                resourceId = nnvl.data.photoResId
            )
        )
    },
    modifier = Modifier.fillMaxWidth(),
    colors = CardDefaults.cardColors(
        containerColor = MaterialTheme.colorScheme.surfaceContainerHigh
    )
) {
    LazyImage(
        id = nnvl.data.photoResId,
        modifier = Modifier.fillMaxWidth(),
        alphaBackground = false
    )
}

@Composable
private fun DevicePreviewScope.CardSecondary(nnvl: DevicePreviewScope.Wrapper) = CommonCardWrapper {
    if (nnvl.canViewLayer) {
        TwoFieldTextIcon(
            label = Res.string.device_detail__layer__top_title.rememberString(),
            labelStyle = LocalTextStyle.current.copy(color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7F)),
            icon = if (nnvl.data.config.layer.top) Svg.Check else Svg.Close
        )
        TwoFieldTextIcon(
            label = Res.string.device_detail__layer__bottom_title.rememberString(),
            labelStyle = LocalTextStyle.current.copy(color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7F)),
            icon = if (nnvl.data.config.layer.bottom) Svg.Check else Svg.Close
        )
        HorizontalDivider()
    }
    @Composable
    fun VideoScale() {
        val scaleValue = remember {
            mutableStateOf("")

        }
        TwoFieldText(
            label = Res.string.device_detail__video_scale_title.rememberString(),
            labelStyle = LocalTextStyle.current.copy(color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7F)),
            message = scaleValue.value
        )
        LaunchedEffect(Unit) {
            val scale = ResolutionStandard[nnvl.data.config.display.videoScale]
            val scalePair = if (scale == ResolutionStandard.UNDEFINED) {
                nnvl.data.config.display.width to nnvl.data.config.display.height
            } else if (
                (minOf(scale.width, scale.height) > minOf(nnvl.data.config.display.width, nnvl.data.config.display.height))
                ||
                (maxOf(scale.width, scale.height) > maxOf(nnvl.data.config.display.width, nnvl.data.config.display.height))
            ) {
                nnvl.data.config.display.width to nnvl.data.config.display.height
            } else if (nnvl.data.config.display.width > nnvl.data.config.display.height) {
                scale.width to scale.height
            } else {
                scale.height to scale.width
            }
            scaleValue.value = if (scalePair.first == nnvl.data.config.display.width && scalePair.second == nnvl.data.config.display.height) {
                Res.string.device_detail__video_scale_auto.await()
            } else {
                "${scalePair.first}x${scalePair.second}"
            }
        }
    }
    if (nnvl.canViewDisplay) {
        TwoFieldText(
            label = Res.string.device_detail__display__rotation_title.rememberString(),
            labelStyle = LocalTextStyle.current.copy(color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7F)),
            message = DeviceEditorScope.Wrapper.RotationDisplay.all[nnvl.data.config.display.rotation].name.rememberString()
        )
        TwoFieldText(
            label = Res.string.device_detail__display__resolution_title.rememberString(),
            labelStyle = LocalTextStyle.current.copy(color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7F)),
            message = "${nnvl.data.config.display.width}x${nnvl.data.config.display.height}"
        )
        TwoFieldText(
            label = Res.string.device_detail__display__aspect_ratio_title.rememberString(),
            labelStyle = LocalTextStyle.current.copy(color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7F)),
            message = ScreenRatio[nnvl.data.config.display.screenRatio].label
        )
        TwoFieldText(
            label = Res.string.device_detail__touch__rotation_title.rememberString(),
            labelStyle = LocalTextStyle.current.copy(color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7F)),
            message = (DeviceEditorScope.Wrapper.RotationTouch.all
                .firstOrNull { it.type match nnvl.data.config.touch.rotation }
                ?: DeviceEditorScope.Wrapper.RotationTouch.all.first()).name.rememberString()
        )
        VideoScale()
        HorizontalDivider()
    }

    if (nnvl.data.config.volume > 0.001F) {
        TwoFieldText(
            label = Res.string.device_detail__info__volume.rememberString(),
            labelStyle = LocalTextStyle.current.copy(color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7F)),
            message = "${nnvl.data.config.volume}%"
        )
    } else {
        TwoFieldTextIcon(
            label = Res.string.device_detail__info__volume.rememberString(),
            labelStyle = LocalTextStyle.current.copy(color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7F)),
            icon = Svg.Close
        )
    }
    TwoFieldTextIcon(
        label = Res.string.device_detail__cursor_visible_title.rememberString(),
        labelStyle = LocalTextStyle.current.copy(color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7F)),
        icon = if (nnvl.data.config.cursorVisible) Svg.Check else Svg.Close
    )
}

@Composable
private fun DevicePreviewScope.CardPrimary(nnvl: DevicePreviewScope.Wrapper) = CommonCardWrapper {
    Text(
        text = nnvl.data.name,
        style = MaterialTheme.typography.headlineMedium,
        fontWeight = FontWeight.Black
    )

    HorizontalDivider()

    Locality(
        nnvl = nnvl,
        openCompany = {
            nav.navTo_CompanyUI(CompanyArgs(companyId = nnvl.data.company.id, forceUpNavigation = true))
        },
        openLocation = {
            nav.navTo_LocationUI(LocationArgs(nnvl.data.company.id, nnvl.data.location.id))
        },
        openGPS = {
            externals.openInBrowser("https://www.google.com/maps/@${nnvl.data.address.lat},${nnvl.data.address.lon},127a,35y,39.47t")
        })

    if (nnvl.data.kioskDefault.id > 0) {
        HorizontalDivider()
        ApplicationLayerDefault(
            enabled = nnvl.canViewLayerTop,
            nnvl = nnvl.data.kioskDefault
        ) {
            nav.navTo_AppDetailUI(
                AppDetailArgs(
                    companyId = nnvl.data.company.id,
                    appId = nnvl.data.kioskDefault.id
                )
            )
        }
    }

    nnvl.data.hardware?.also { variant ->
        HorizontalDivider()

        ElevatedCard(
            enabled = nnvl.canViewHW,
            onClick = {
                nav.navTo_HardwareDetailUI(
                    HardwareDetailArgs(variant.id)
                )
            },
            colors = CardDefaults.elevatedCardColors(
                containerColor = MaterialTheme.colorScheme.surfaceContainerHigh
            ),
            modifier = Modifier.fillMaxWidth(),
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier.padding(end = 8.dp)
            ) {
                LazyImage(
                    id = variant.image,
                    modifier = Modifier.size(96.dp),
                    alphaBackground = false,
                    contentScale = ContentScale.Crop
                )
                Column(
                    modifier = Modifier
                        .padding(8.dp)
                        .weight(1F)
                        .align(Alignment.CenterVertically)
                ) {
                    Text(variant.name, maxLines = 1)
                    if (variant.name != variant.subtitle) {
                        Text(variant.subtitle, maxLines = 1)
                    }
                }
                if (nnvl.canViewHW) Icon(
                    imageVector = Svg.Chevron_right,
                    contentDescription = null,
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
            }
        }
    }
}

@Composable
private fun ApplicationLayerDefault(
    enabled: Boolean,
    nnvl: Io.DeviceInfo.KioskDefault, onClick: () -> Unit
) {
    ElevatedCard(
        enabled = enabled,
        onClick = onClick,
        colors = CardDefaults.elevatedCardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainerHigh
        )
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier.padding(8.dp)
        ) {
            LazyImage(
                id = nnvl.logo,
                modifier = Modifier.align(Alignment.CenterVertically).size(32.dp),
                alphaBackground = false
            )
            Column(
                Modifier.align(Alignment.CenterVertically).weight(1F)
            ) {
                Text(
                    text = "Application layer startup",
                    style = MaterialTheme.typography.labelSmall,
                    modifier = Modifier.alpha(0.7F)
                )
                Text(
                    text = nnvl.name,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }

            if (enabled) Icon(
                imageVector = Svg.Chevron_right,
                modifier = Modifier.align(Alignment.CenterVertically),
                contentDescription = null
            )
        }
    }
}

@Composable
private fun Locality(
    nnvl: DevicePreviewScope.Wrapper,
    openCompany: () -> Unit,
    openLocation: () -> Unit,
    openGPS: () -> Unit
) {
    if (nnvl.canViewCompany || nnvl.canViewLocation) {
        Column {
            LabeledButton(
                label = nnvl.data.company.name,
                enabled = nnvl.canViewCompany,
                onClick = openCompany
            )
            LabeledButton(
                label = nnvl.data.location.name,
                enabled = nnvl.canViewLocation,
                onClick = openLocation
            )
            LabeledButton(
                label = "${nnvl.data.address.lat}, ${nnvl.data.address.lon}",
                enabled = true,
                onClick = openGPS
            )
        }
    }
}

@Composable
private fun DevicePreviewScope.CardEmergency(nnvl: DevicePreviewScope.Wrapper) {
    Card(
        modifier = Modifier.size(512.dp),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.errorContainer,
            contentColor = MaterialTheme.colorScheme.onErrorContainer
        )
    ) {
        Box(Modifier.fillMaxSize()) {
            LazyImage(
                id = nnvl.data.emergency.image,
                modifier = Modifier.fillMaxSize(),
                alphaBackground = false,
                contentScale = ContentScale.Crop
            )
            Text(
                text = Res.string.location_detail__emergency_status_message.rememberString(),
                style = MaterialTheme.typography.titleLarge,
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth()
                    .background(Color.Black.copy(alpha = 0.8F))
                    .padding(16.dp)
            )
            Column(
                verticalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier
                    .background(Color.Black.copy(alpha = 0.8F))
                    .padding(16.dp)
                    .align(Alignment.BottomCenter)
                    .fillMaxWidth()
            ) {
                Button(
                    onClick = {
                        nav.findRoot().navTo_LocationUI(
                            LocationArgs(
                                companyId = nnvl.data.company.id,
                                locationId = nnvl.data.location.id
                            )
                        )
                    },
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Text(
                        text = Res.string.device_detail__emergency_disable_action.rememberString(),
                        fontWeight = FontWeight.Bold
                    )
                }
            }
        }
    }
}

@Composable
private fun LabeledButton(label: String, enabled: Boolean, onClick: () -> Unit) {
    ElevatedCard(
        onClick = onClick,
        enabled = enabled,
        colors = CardDefaults.elevatedCardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainerHigh
        )
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier.padding(8.dp)
        ) {
            Text(
                text = label,
                modifier = Modifier.weight(1F)
            )
            if (enabled) Icon(Svg.Chevron_right, null)
        }
    }
}

@Composable
private fun CommonCardWrapper(body: @Composable ColumnScope.() -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(24.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp),
            content = body
        )
    }
}
